import { supabase } from '@/integrations/supabase/client';
import { prepareImageForUpload } from '@/lib/imageUtils';
import { SOCIAL_FIELDS, normalizeSocialUrl } from '@/lib/profileSocials';

export interface UserProfile {
  id: string;
  user_id: string;
  display_name: string | null;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  timezone: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  linkedin_url: string | null;
  tiktok_url: string | null;
  phone_country_code: string | null;
  phone_number: string | null;
  seller_type_id: string | null;
  seller_expires_at: string | null;
  is_verified: boolean;
  verification_expires_at: string | null;
  seller_suspended_at: string | null;
  student_lost_pending: boolean;
  welcome_onboarding_accepted_at: string | null;
  welcome_onboarding_version: number;
  seller_onboarding_accepted_at: string | null;
  push_community_enabled: boolean;
  push_news_enabled: boolean;
  push_events_enabled: boolean;
  boost_plan_total: number;
  created_at: string;
  updated_at: string;
  student_since: string | null;
}

export interface SellerStats {
  seller_id: string;
  completed_sales: number;
  average_rating: number | null;
  total_ratings: number;
}

// Optimized column selections
// NOTE: phone_country_code, phone_number e full_name são intencionalmente
// excluídos do SELECT amplo — grants em nível de coluna restringem essas
// colunas ao dono/admin via funções SECURITY DEFINER
// (public.get_profile_phone e public.get_profile_full_name).
const PROFILE_COLUMNS = `id, user_id, display_name, avatar_url, bio, location, timezone,
  instagram_url, youtube_url, linkedin_url, tiktok_url,
  seller_type_id, seller_expires_at, is_verified, verification_expires_at, seller_suspended_at,
  boost_plan_total, created_at, updated_at, student_since`;
const PUBLIC_PROFILE_MINI_COLUMNS = 'avatar_url, display_name, bio, instagram_url, youtube_url, linkedin_url, tiktok_url';
const SELLER_STATS_COLUMNS = 'seller_id, completed_sales, average_rating, total_ratings';

export interface PublicProfileMini {
  avatar_url: string | null;
  display_name: string | null;
  bio: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  linkedin_url: string | null;
  tiktok_url: string | null;
}

// ─── Search & lightweight queries ────────────────────────────

export interface ProfileSuggestion {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
}

/**
 * Searches profiles by partial display_name match. Used by mention autocomplete.
 */
export async function searchProfilesByName(query: string, limit = 5): Promise<ProfileSuggestion[]> {
  if (!query || query.length < 2) return [];
  const { data, error } = await (supabase as any).rpc('search_community_members', {
    p_query: query,
    p_limit: limit,
  });
  if (error) throw error;
  return (Array.isArray(data) ? data : []) as ProfileSuggestion[];
}

/**
 * Returns counts for push subscriptions and total profiles. Used by
 * the admin "App Maintenance" reach indicator.
 */
export async function fetchPushAndProfileCounts(): Promise<{ subs: number | null; profiles: number | null }> {
  // Push remains deliberately disabled during the cutover. Do not query the
  // legacy subscriptions/profile tables.
  return { subs: null, profiles: null };
}

export async function fetchPublicProfileMini(userId: string): Promise<PublicProfileMini | null> {
  void PUBLIC_PROFILE_MINI_COLUMNS;
  const { data, error } = await (supabase as any).rpc('get_forjahub_public_profile', {
    p_user_id: userId,
  });

  if (error) throw error;
  return data as PublicProfileMini | null;
}

// Campos privados do perfil (preferências e onboarding) só são legíveis pelo
// próprio dono, via função SECURITY DEFINER — os grants de coluna bloqueiam
// a leitura desses campos por SELECT direto.
type PrivateProfileFields = Pick<UserProfile,
  'push_community_enabled' | 'push_news_enabled' | 'push_events_enabled' |
  'welcome_onboarding_accepted_at' | 'welcome_onboarding_version' |
  'seller_onboarding_accepted_at' | 'student_lost_pending'>;

const PRIVATE_DEFAULTS: PrivateProfileFields = {
  push_community_enabled: true,
  push_news_enabled: true,
  push_events_enabled: true,
  welcome_onboarding_accepted_at: null,
  welcome_onboarding_version: 0,
  seller_onboarding_accepted_at: null,
  student_lost_pending: false,
};

export async function fetchMyProfilePrivate(): Promise<PrivateProfileFields> {
  try {
    const { data } = await (supabase as any).rpc('get_current_forjahub_profile_details');
    const row = Array.isArray(data) ? (data[0] as any) : data;
    return row ? { ...PRIVATE_DEFAULTS, ...row } : PRIVATE_DEFAULTS;
  } catch {
    return PRIVATE_DEFAULTS;
  }
}

export async function fetchUserProfile(userId: string): Promise<UserProfile | null> {
  void PROFILE_COLUMNS;
  const { data, error } = await (supabase as any).rpc('get_current_forjahub_profile_details');

  if (error) throw error;
  if (!data) return null;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || row.user_id !== userId) return null;
  const { data: seller } = await (supabase as any)
    .from('seller_credits_status')
    .select('is_verified, verification_expires_at, seller_type_name')
    .eq('user_id', userId)
    .maybeSingle();
  return {
    ...row,
    is_verified: Boolean(seller?.is_verified),
    verification_expires_at: seller?.verification_expires_at ?? null,
    seller_type_id: seller?.seller_type_name ?? null,
  } as UserProfile;
}

export async function updateUserProfile(
  userId: string,
  updates: Partial<Pick<UserProfile, 'display_name' | 'full_name' | 'bio' | 'location' | 'avatar_url' | 'timezone' | 'instagram_url' | 'youtube_url' | 'linkedin_url' | 'tiktok_url' | 'phone_country_code' | 'phone_number' | 'push_community_enabled' | 'push_news_enabled' | 'push_events_enabled'>>
): Promise<UserProfile> {
  // Normalise social URLs before persisting (strip tracking params, enforce
  // https://, validate host). Invalid input becomes null instead of breaking.
  const sanitized: Record<string, unknown> = { ...updates };
  for (const field of SOCIAL_FIELDS) {
    if (field.key in sanitized) {
      const raw = sanitized[field.key] as string | null | undefined;
      sanitized[field.key] = raw == null || raw === '' ? null : normalizeSocialUrl(raw, field);
    }
  }

  const current = await fetchUserProfile(userId);
  const { data, error } = await (supabase as any).rpc('update_current_forjahub_profile_details', {
    p_profile: { ...(current || {}), ...sanitized },
  });

  if (error) throw error;
  return (Array.isArray(data) ? data[0] : data) as UserProfile;
}

export async function uploadAvatar(userId: string, file: File): Promise<string> {
  // Compress image before upload (256x256, webp, ~15-30KB)
  const { blob, extension, mimeType } = await prepareImageForUpload(file, 'avatar');
  const fileName = `${userId}/${Date.now()}.${extension}`;

  // Upload the new avatar FIRST — only after a successful upload do we remove
  // the previous files. This way a failed upload leaves the old avatar intact.
  const { error } = await supabase.storage
    .from('forjahub-profile-media')
    .upload(fileName, blob, { 
      upsert: true,
      contentType: mimeType
    });

  if (error) throw error;

  // Best-effort cleanup of previous files (don't block on failure).
  try {
    const { data: existingFiles } = await supabase.storage
      .from('forjahub-profile-media')
      .list(userId);
    if (existingFiles && existingFiles.length > 0) {
      const stale = existingFiles
        .map((f) => `${userId}/${f.name}`)
        .filter((path) => path !== fileName);
      if (stale.length > 0) {
        await supabase.storage.from('forjahub-profile-media').remove(stale);
      }
    }
  } catch {
    // ignore — avatar already uploaded successfully
  }

  const { data: { publicUrl } } = supabase.storage
    .from('forjahub-profile-media')
    .getPublicUrl(fileName);

  return publicUrl;
}

export async function fetchSellerStats(userId: string): Promise<SellerStats | null> {
  void SELLER_STATS_COLUMNS;
  const { data, error } = await (supabase as any).from('user_reputation').select('*').eq('user_id', userId).maybeSingle();
  if (error || !data) return null;
  return { seller_id: userId, completed_sales: Number(data.completed_negotiations || 0), average_rating: Number(data.average_rating || 0), total_ratings: Number(data.total_ratings || 0) };
}

export async function fetchActiveListingsCount(userId: string): Promise<number> {
  const { count } = await (supabase as any).from('marketplace_listings').select('id', { count: 'exact', head: true }).eq('seller_id', userId).eq('status', 'active');
  return count || 0;
}

